package de.oevbs.kunde.kundenportal2.navigation

import kotlinx.browser.window

/**
 * Ein Ziel (intern oder extern), zu dem navigiert werden kann.
 *
 * Diese Abstraktion ermöglicht es, in der Navigation verschiedene seiteninterne Routen und externe URLs einheitlich
 * einzubinden und über eine gemeinsame Schnittstelle ungeachtet ihrer konkreten Ausprägung anzusteuern.
 */
sealed interface NavigationTarget {

    /**
     * Absolute URL, auf das das [NavigationTarget] zeigt.
     *
     * Zur Navigation sollte normalerweise [visit] verwendet werden, da diese Methode die spezifischen Eigenheiten
     * der jeweiligen Ausprägung berücksichtigt. In einigen Ausnahmefällen kann es jedoch erforderlich sein, manuell
     * auf die URL zuzugreifen.
     */
    val url: String

    /**
     * Navigiert zum Target.
     */
    fun visit()

    /**
     * Ein kundenportal-internes Target, das über den im Projekt verwendeten Router angesteuert wird.
     *
     * Das Target wird über die angegebene [route] angegeben, die vom hinterlegten [router] angesteuert wird.
     */
    class Internal(private val router: KupoRouter, private val route: String) : NavigationTarget {

        override fun visit() = router.navToPageRoute(route)

        /**
         * Gibt dynamisch die komplette URL bestehend aus der vom Router verwendeten [route] der Page und der restlichen
         * URL der Seite zurück.
         *
         * Diese Property kann bspw. dann verwendet werden, wenn ein HTML-Anchor-Element mit Link zu Navigation
         * verwendet werden soll, anstatt [NavigationTarget.visit] aufzurufen.
         *
         * Beispiel:
         * ```
         * val target = NavigationTarget.Internal(theRouter, "#test")
         * println(target.url)
         * // https://entw.kunde.oeffentliche.de/index.html#test
         * ```
         */
        override val url: String
            get() = window.location.run { "$origin$pathname#${KupoRouter.PAGE_ROUTE_KEY}=$route" }
    }

    /**
     * Ein über die angegebene [url] definiertes, externes Target.
     *
     * Wird das Target angesteuert, öffnet sich die hinterlegte URL in einem neuen Tab.
     */
    class External(override val url: String) : NavigationTarget {
        override fun visit() {
            window.open(url, target = "_blank")
        }
    }
}